import { alpha, keyframes } from '@mui/material';

// Animación para el logo
export const logoFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-2px); }
`;

// ─── Static fallback tokens (light) ─────────────────────────
export const designTokens = {
  primary: '#007CA2',
  primaryDark: '#005a7a',
  text: {
    primary: '#2c3e50',
  },
  background: {
    paper: '#ffffff',
    hover: alpha('#007CA2', 0.08),
    hoverLight: alpha('#ffffff', 0.1),
    active: alpha('#ffffff', 0.2),
  },
  border: {
    primary: alpha('#007CA2', 0.2),
    active: alpha('#ffffff', 0.3),
  },
  shadow: {
    default: '0 2px 8px rgba(0, 0, 0, 0.1)',
    elevated: '0 4px 20px rgba(0, 124, 162, 0.3)',
    button: '0 4px 12px rgba(255, 255, 255, 0.2)',
    menu: '0 8px 24px rgba(0, 40, 60, 0.1)',
  },
};

// ─── Dynamic token builder ───────────────────────────────────
export const getDesignTokens = (themeColors) => {
  if (!themeColors) return designTokens;
  const nav = themeColors.navBar || {};
  return {
    primary: themeColors.primary,
    primaryDark: themeColors.primaryDark,
    text: { primary: nav.menuText || themeColors.text.primary },
    background: {
      paper: themeColors.background?.paper || '#ffffff',
      hover: nav.menuHover || alpha(themeColors.primary, 0.08),
      hoverLight: nav.hoverBg || alpha('#ffffff', 0.1),
      active: nav.activeBg || alpha('#ffffff', 0.2),
    },
    border: {
      primary: nav.menuBorder || alpha(themeColors.primary, 0.2),
      active: nav.borderActive || alpha('#ffffff', 0.3),
    },
    shadow: {
      ...designTokens.shadow,
      menu: nav.menuShadow || designTokens.shadow.menu,
    },
  };
};

// ─── Dynamic style builders ──────────────────────────────────
export const getAppBarStyles = (themeColors) => {
  const nav = themeColors?.navBar;
  return {
    background: nav?.gradient || designTokens.primary,
  };
};

export const getMenuPaperStyles = (themeColors) => {
  const tokens = getDesignTokens(themeColors);
  const nav = themeColors?.navBar;
  return {
    pointerEvents: 'auto',
    borderRadius: 2,
    minWidth: 220,
    background: nav?.menuBg || tokens.background.paper,
    boxShadow: tokens.shadow.menu,
    border: `1px solid ${tokens.border.primary}`,
    '& .MuiList-root': {
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  };
};

export const getMenuItemStyles = (themeColors) => {
  const tokens = getDesignTokens(themeColors);
  return {
    px: 2,
    py: 1.25,
    borderRadius: 1.5,
    mx: 1,
    my: 0.5,
    fontSize: '0.95rem',
    fontWeight: 500,
    color: tokens.text.primary,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: tokens.background.hover,
      color: tokens.primary,
    },
    '&:first-of-type': { mt: 1 },
    '&:last-of-type': { mb: 1 },
  };
};

// ─── Static styles (unchanged) ───────────────────────────────

// Estilos del AppBar (static fallback)
export const appBarStyles = {
  background: designTokens.primary,
  boxShadow: designTokens.shadow.default,
};

// Estilos del Toolbar
export const toolbarStyles = {
  py: 0.5,
};

// Estilos del IconButton (Home y Logout)
export const iconButtonStyles = {
  borderRadius: 2,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: designTokens.background.hoverLight,
    transform: 'translateY(-1px)',
  },
};

// Estilos del Home Icon
export const homeIconStyles = {
  ...iconButtonStyles,
  mr: 2,
};

// Estilos del contenedor del Logo
export const logoBoxStyles = {
  height: '50px',
  width: '50px',
  mr: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
};

// Estilos del Logo SVG
export const logoSvgStyles = {
  width: '100%',
  height: '100%',
};

// Estilos del Typography del título
export const titleTypographyStyles = {
  flexGrow: 1,
  fontWeight: 500,
  color: 'white',
  fontSize: '1.1rem',
};

// Estilos de botones de navegación (comunes)
export const navButtonBaseStyles = {
  mr: 2,
  px: 3,
  py: 1,
  borderRadius: 2,
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '1rem',
  border: '1px solid transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: designTokens.background.hoverLight,
    transform: 'translateY(-1px)',
    boxShadow: designTokens.shadow.button,
  },
};

// Función para obtener estilos de botón según estado activo
export const getNavButtonStyles = (isActive) => ({
  ...navButtonBaseStyles,
  backgroundColor: isActive
    ? designTokens.background.active
    : 'transparent',
  border: isActive
    ? `1px solid ${designTokens.border.active}`
    : '1px solid transparent',
});

// Estilos para el botón del menú desplegable
export const getMenuButtonStyles = (isOpen) => ({
  mr: 2,
  px: 3,
  py: 1,
  borderRadius: 2,
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '1rem',
  backgroundColor: isOpen ? designTokens.background.hover : 'transparent',
  border: '1px solid transparent',
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: designTokens.background.hover,
  },
});

// Estilos del Paper del Menu
export const menuPaperStyles = {
  pointerEvents: 'auto',
  borderRadius: 2,
  minWidth: 220,
  background: designTokens.background.paper,
  boxShadow: designTokens.shadow.menu,
  border: `1px solid ${designTokens.border.primary}`,
  '& .MuiList-root': {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
};

// Estilos de los MenuItem
export const menuItemStyles = {
  px: 2,
  py: 1.25,
  borderRadius: 1.5,
  mx: 1,
  my: 0.5,
  fontSize: '0.95rem',
  fontWeight: 500,
  color: designTokens.text.primary,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: designTokens.background.hover,
    color: designTokens.primary,
  },
  '&:first-of-type': { mt: 1 },
  '&:last-of-type': { mb: 1 },
};

// Estilos para MenuItem con iconos (usado en el developer navbar)
export const menuItemWithIconStyles = {
  ...menuItemStyles,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  '&:hover': {
    ...menuItemStyles['&:hover'],
    transform: 'translateX(4px)',
  },
};

// Estilos del ExpandMore Icon
export const expandIconStyles = (isOpen) => ({
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease',
});

// Estilos para iconos dentro de MenuItem
export const menuIconStyles = {
  fontSize: 20,
  color: designTokens.primary,
};
